import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, ToggleButtonGroup, ToggleButton, Paper, useTheme,
} from '@mui/material';
import Chart from 'react-apexcharts';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { LoadingOverlay, EmptyState } from 'dashboard/components/common';

const fmtMoney = (amount, ccy) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (ccy || 'usd').toUpperCase(),
    }).format(amount || 0);
  } catch (_) {
    return `${(amount || 0).toFixed(2)} ${ccy || ''}`;
  }
};

export default function EarningsTab() {
  const [granularity, setGranularity] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    API.adminEarnings({ granularity, months: granularity === 'year' ? 60 : 12 })
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => toast.error(err?.message || 'Failed to load earnings', { toastId: 'admin-earnings' }))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [granularity]);

  const theme = useTheme();

  const total = useMemo(() => {
    if (!data?.buckets) return 0;
    return data.buckets.reduce((sum, b) => sum + (b.amount || 0), 0);
  }, [data]);

  // ApexCharts draws its own axis text and grid, outside MUI entirely, so a
  // dark page would otherwise get near-black tick labels on a near-black
  // ground. Everything chrome-coloured here comes from the theme.
  const chartOptions = useMemo(() => ({
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'inherit',
      foreColor: theme.palette.text.secondary,
    },
    xaxis: {
      categories: data?.buckets?.map((b) => b.key) || [],
      labels: { rotate: -30, style: { fontSize: '11px' } },
    },
    yaxis: {
      labels: {
        formatter: (v) => fmtMoney(v, data?.currency),
      },
    },
    dataLabels: { enabled: false },
    stroke: { width: 3, curve: 'smooth' },
    colors: [theme.palette.primary.main],
    tooltip: {
      theme: theme.palette.mode,
      y: { formatter: (v) => fmtMoney(v, data?.currency) },
    },
    grid: { borderColor: theme.palette.divider },
  }), [data, theme]);

  const chartSeries = useMemo(() => ([{
    name: 'Earnings',
    data: data?.buckets?.map((b) => b.amount) || [],
  }]), [data]);

  if (data && data.stripe === false) {
    return <EmptyState>Stripe isn't configured on this server. Earnings come from Stripe charges.</EmptyState>;
  }

  return (
    <Box>
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb='14px' flexWrap='wrap' gap='10px'>
        <ToggleButtonGroup
          size='small'
          exclusive
          value={granularity}
          onChange={(_e, v) => v && setGranularity(v)}
        >
          <ToggleButton value='week'>Weekly</ToggleButton>
          <ToggleButton value='month'>Monthly</ToggleButton>
          <ToggleButton value='year'>Yearly</ToggleButton>
        </ToggleButtonGroup>
        <Stack alignItems='flex-end'>
          <Typography variant='caption' color='text.secondary' textTransform='uppercase' fontWeight='700'>
            Total in period
          </Typography>
          <Typography fontWeight='700'>{fmtMoney(total, data?.currency)}</Typography>
        </Stack>
      </Stack>

      <LoadingOverlay loading={loading} minHeight={320}>
        {data?.buckets?.length ? (
          <Paper elevation={0} sx={{ borderRadius: '16px', p: '16px' }}>
            <Chart options={chartOptions} series={chartSeries} type='area' height={320} />
          </Paper>
        ) : (!loading && <EmptyState>No earnings in the selected window.</EmptyState>)}
      </LoadingOverlay>
    </Box>
  );
}
